
import { worldCountries } from './countries/index';

export interface City {
  id: string;
  name: string;
  country: string;
  countryCode: string;
  population: number;
  coordinates: {
    lat: number;
    lng: number;
  };
  language: string;
  timezone: string;
  slug: string;
  state_province?: string;
}

// Generate cities from the expanded country data
const generateCitiesFromCountries = () => {
  const cities: City[] = [];
  
  worldCountries.forEach(country => {
    country.topCities.forEach(city => {
      // Generate basic coordinates (this would ideally come from a geocoding service)
      const baseCoords = getCountryCoordinates(country.code);
      const cityCoords = {
        lat: baseCoords.lat + (Math.random() - 0.5) * 2, // Random offset within country
        lng: baseCoords.lng + (Math.random() - 0.5) * 2
      };
      
      cities.push({
        id: `${city.slug}-${country.code.toLowerCase()}`,
        name: city.name,
        country: country.name,
        countryCode: country.code,
        population: city.population,
        coordinates: cityCoords,
        language: getCountryLanguage(country.code),
        timezone: getCountryTimezone(country.code),
        slug: city.slug
      });
    });
  });
  
  return cities;
};

// Helper function to get approximate country coordinates
function getCountryCoordinates(countryCode: string): { lat: number; lng: number } {
  const countryCoords: { [key: string]: { lat: number; lng: number } } = {
    'US': { lat: 39.8283, lng: -98.5795 },
    'CN': { lat: 35.8617, lng: 104.1954 },
    'IN': { lat: 20.5937, lng: 78.9629 },
    'BR': { lat: -14.2350, lng: -51.9253 },
    'MX': { lat: 23.6345, lng: -102.5528 },
    'EG': { lat: 26.0975, lng: 31.4478 },
    'JP': { lat: 36.2048, lng: 138.2529 },
    'BD': { lat: 23.6850, lng: 90.3563 },
    'PK': { lat: 30.3753, lng: 69.3451 },
    'AR': { lat: -38.4161, lng: -63.6167 },
    'TR': { lat: 38.9637, lng: 35.2433 },
    'PH': { lat: 12.8797, lng: 121.7740 },
    'NG': { lat: 9.0820, lng: 8.6753 },
    'ES': { lat: 40.4637, lng: -3.7492 },
    'GB': { lat: 55.3781, lng: -3.4360 },
    'FR': { lat: 46.6034, lng: 1.8883 },
    'DE': { lat: 51.1657, lng: 10.4515 },
    'IT': { lat: 41.8719, lng: 12.5674 },
    'NL': { lat: 52.1326, lng: 5.2913 },
    'AU': { lat: -25.2744, lng: 133.7751 },
    'NZ': { lat: -40.9006, lng: 174.8860 },
    'SA': { lat: 23.8859, lng: 45.0792 },
    'IR': { lat: 32.4279, lng: 53.6880 },
    'IQ': { lat: 33.2232, lng: 43.6793 },
    'AE': { lat: 23.4241, lng: 53.8478 },
    'IL': { lat: 31.0461, lng: 34.8516 },
    'JO': { lat: 30.5852, lng: 36.2384 },
    'LB': { lat: 33.8547, lng: 35.8623 },
    'SY': { lat: 34.8021, lng: 38.9968 },
    'YE': { lat: 15.5527, lng: 48.5164 },
    'OM': { lat: 21.4735, lng: 55.9754 },
    'KW': { lat: 29.3117, lng: 47.4818 },
    'BH': { lat: 25.9304, lng: 50.6378 },
    'QA': { lat: 25.3548, lng: 51.1839 },
    'PS': { lat: 31.9522, lng: 35.2332 },
    'CY': { lat: 35.1264, lng: 33.4299 }
  };
  
  return countryCoords[countryCode] || { lat: 0, lng: 0 };
}

// Helper function to get country language
function getCountryLanguage(countryCode: string): string {
  const countryLanguages: { [key: string]: string } = {
    'US': 'en', 
    'CA': 'en', 
    'GB': 'en', 
    'AU': 'en', 
    'NZ': 'en', 
    'IE': 'en',
    'ES': 'es', 
    'MX': 'es', 
    'AR': 'es', 
    'CO': 'es', 
    'PE': 'es', 
    'VE': 'es', 
    'CL': 'es',
    'FR': 'fr', 
    'BE': 'fr', 
    'CH': 'fr',
    'DE': 'de', 
    'AT': 'de',
    'IT': 'it',
    'PT': 'pt', 
    'BR': 'pt', 
    'AO': 'pt', 
    'MZ': 'pt',
    'NL': 'nl',
    'CN': 'zh', 
    'TW': 'zh', 
    'HK': 'zh', 
    'SG': 'zh',
    'JP': 'ja',
    'KR': 'ko',
    'IN': 'hi', 
    'PK': 'ur', 
    'BD': 'bn',
    'RU': 'ru', 
    'BY': 'ru', 
    'KZ': 'ru',
    'SA': 'ar', 
    'EG': 'ar', 
    'IQ': 'ar', 
    'SY': 'ar', 
    'JO': 'ar', 
    'LB': 'ar', 
    'YE': 'ar',
    'IR': 'fa', 
    'AF': 'fa',
    'TR': 'tr',
    'TH': 'th', 
    'VN': 'vi', 
    'ID': 'id', 
    'MY': 'ms', 
    'PH': 'en'
  };
  
  return countryLanguages[countryCode] || 'en';
}

// Helper function to get country timezone
function getCountryTimezone(countryCode: string): string {
  const countryTimezones: { [key: string]: string } = {
    'US': 'America/New_York',
    'CA': 'America/Toronto',
    'MX': 'America/Mexico_City',
    'BR': 'America/Sao_Paulo',
    'AR': 'America/Argentina/Buenos_Aires',
    'GB': 'Europe/London',
    'FR': 'Europe/Paris',
    'DE': 'Europe/Berlin',
    'ES': 'Europe/Madrid',
    'IT': 'Europe/Rome',
    'NL': 'Europe/Amsterdam',
    'CN': 'Asia/Shanghai',
    'JP': 'Asia/Tokyo',
    'IN': 'Asia/Kolkata',
    'PK': 'Asia/Karachi',
    'BD': 'Asia/Dhaka',
    'TH': 'Asia/Bangkok',
    'VN': 'Asia/Ho_Chi_Minh',
    'ID': 'Asia/Jakarta',
    'PH': 'Asia/Manila',
    'AU': 'Australia/Sydney',
    'NZ': 'Pacific/Auckland',
    'SA': 'Asia/Riyadh',
    'AE': 'Asia/Dubai',
    'EG': 'Africa/Cairo',
    'NG': 'Africa/Lagos',
    'ZA': 'Africa/Johannesburg'
  };
  
  return countryTimezones[countryCode] || 'UTC';
}

// Generate the cities array from country data
export const worldCities: City[] = generateCitiesFromCountries();

/**
 * @deprecated Use useCityData() hook instead for pages/components
 * This function only searches in static worldCities array (~500 cities).
 * Most cities (32,853) are in Supabase database, not here.
 * 
 * Migration guide:
 * import { useCityData } from '@/hooks/useCityData';
 * const { data: city, isLoading } = useCityData(citySlug);
 */
export const getCityBySlug = (slug: string): City | undefined => {
  return worldCities.find(city => city.slug === slug);
};

export const getCitiesByCountry = (countryCode: string): City[] => {
  return worldCities.filter(city => city.countryCode === countryCode);
};

export const getCitiesByLanguage = (language: string): City[] => {
  return worldCities.filter(city => city.language === language);
};
